import { useState, useCallback, useRef, useMemo } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { probeMedia } from "@/lib/ffmpegEngine";
import { loadFFmpeg, execWithProgress, compressVideoToTarget, fetchFile, checkFileSize, ffmpegOutputToBlob, stopProcessing, resetFFmpegEngine } from "./operations";
import { BYTES_PER_MB, toMB } from "@/lib/megabytes";
import { VideoProcessingProgress, showVideoError, FileSizeNotice } from "./uiHelpers";
import { looksLikeVideo, type VideoTrim } from "./utils";
import { parseTime } from "./timeParse";
import { findMp4Problem } from "./mp4Structure";
import { AlertTriangle } from "lucide-react";
import { Target } from "lucide-react";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Reduces a video file's size, typically to fit an upload limit or speed up sharing, by re-encoding it at a lower quality level. Medium/Strong aim for at least a 25%/50% smaller file using the same size-targeting encode as Custom Target, instead of a vague quality label. Some videos are already compressed so well that they cannot be made smaller; the tool tells you and offers your original back. "Manual Quality" gives direct slider control if you'd rather not use a percentage target. Runs entirely in your browser — the video never leaves your device.`,
  how: `Upload your video. Pick Medium or Strong for a percentage reduction, use Manual Quality for direct slider control, or switch to Custom Target and enter an exact size limit (like 25MB for a Discord upload).`,
  tip: `Compression re-encodes every frame of the video, which is the most processing-intensive thing any video tool here does — Medium/Strong and Custom Target aim at an exact size and may need a second encode to land under it, so expect them to take real time, especially for longer or higher-resolution videos. There's no automatic cutoff, so let it run — use Stop if you'd rather not wait. Some phone-recorded videos (particularly .mov files) use a codec this tool can't read — if processing fails right away, try re-exporting the video from your phone's own share options first.`,
};

// FFmpeg's CRF scale: lower = higher quality/larger file, higher = more
// compression/smaller file. 18 is near-lossless, 28 is noticeably
// compressed but still watchable -- this range covers "barely smaller"
// to "clearly smaller" without going so high the result looks broken.
function sliderToCrf(sliderValue: number): number {
  return Math.round(18 + (sliderValue / 100) * 14);
}

type Mode = "medium" | "strong" | "quality" | "target";
// Guaranteed reduction targets -- computed from the current file's size
// and run through the same proven two-pass target-search algorithm as
// the manual Target Size mode, so these are as reliable as that mode.
const PERCENT_TARGETS: Partial<Record<Mode, number>> = {
  medium: 0.75, // 25% reduction
  strong: 0.5, // 50% reduction
};
const TARGET_PRESETS = [
  { label: "8 MB (Discord)", bytes: 8 * 1_000_000 },
  { label: "16 MB (WhatsApp)", bytes: 16 * 1_000_000 },
  { label: "25 MB (email)", bytes: 25 * 1_000_000 },
  { label: "50 MB", bytes: 50 * 1_000_000 },
  { label: "100 MB", bytes: 100 * 1_000_000 },
];

export default function CompressVideo() {
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<Mode>("medium");
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const [quality, setQuality] = useState([50]);
  const [targetMB, setTargetMB] = useState("25");
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<{ blob: Blob; size: number; metTarget?: boolean; targetBytes?: number } | null>(null);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [lowerRes, setLowerRes] = useState(false);
  const [trimStart, setTrimStart] = useState("");
  const [trimEnd, setTrimEnd] = useState("");
  const startedAt = useRef(0);
  const origUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0]; if (!f) return;
    if (!looksLikeVideo(f)) { toast.error(`"${f.name}" isn't a video file`, { description: "Drop an MP4, MOV, WebM, MKV or similar video." }); return; }
    // A cut-off or damaged file is named straight away, before any minute-long encode.
    let problem: string | null = null;
    try { problem = await findMp4Problem(f); } catch { /* unreadable header: let the encoder decide */ }
    if (problem) toast.error(`"${f.name}" can't be compressed`, { description: problem, duration: 10000 });
    setFileProblem(problem);
    setFile(f); setResult(null);
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) stopProcessing(ffmpegRef.current);
  };

  const compress = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading video engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading video file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp4";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      if (mode === "target" || mode === "medium" || mode === "strong") {
        let targetBytes: number;
        if (mode === "target") {
          targetBytes = Math.round(parseFloat(targetMB || "0") * BYTES_PER_MB);
          if (!targetBytes || targetBytes <= 0) {
            toast.error("Enter a valid target size");
            setProcessing(false);
            return;
          }
        } else {
          targetBytes = Math.round(file.size * (PERCENT_TARGETS[mode] as number));
        }
        // Optional trim (Custom Target only): the target is for the kept part.
        let trim: VideoTrim | undefined;
        if (mode === "target" && (trimStart.trim() || trimEnd.trim())) {
          const start = trimStart.trim() ? parseTime(trimStart) : 0;
          const end = trimEnd.trim() ? parseTime(trimEnd) : undefined;
          if (Number.isNaN(start) || (end !== undefined && Number.isNaN(end)) || start < 0 || (end !== undefined && end <= start)) {
            toast.error("Check the trim times", { description: "Use seconds or m:ss, for example 0:30 and 2:15, with End later than Start." });
            setProcessing(false);
            return;
          }
          trim = { start, end };
        }
        startedAt.current = Date.now();
        setStatusMessage("Encoding (0%)...");
        const outcome = await compressVideoToTarget(ffmpeg, `input.${ext}`, "output.mp4", targetBytes, (f, attempt) => {
          setProgress(f);
          const elapsed = (Date.now() - startedAt.current) / 1000;
          const left = f > 0.03 && attempt === 1 ? Math.max(0, elapsed / f - elapsed) : null;
          setStatusMessage(`${attempt === 2 ? "Fine-tuning to reach the target size" : "Encoding"}${left ? ` - about ${left > 90 ? Math.round(left / 60) + " min" : Math.round(left) + " s"} left` : ""}`);
        }, controller.signal, lowerRes ? 720 : undefined, trim);
        setResult(outcome);
        const pct = ((1 - outcome.size / file.size) * 100).toFixed(1);
        if (outcome.metTarget) toast.success(`Compressed to ${toMB(outcome.size, 1)} MB (${pct}% smaller).`);
        else toast.warning(`Got it to ${toMB(outcome.size, 1)} MB (${pct}% smaller) - this missed the target. Try a stronger setting or "Lower resolution to 720p".`);
      } else {
        setStatusMessage("Compressing (this is the slowest video operation)...");
        const crf = sliderToCrf(quality[0]);
        // Never let the picture use more bits than the source did (a high-quality CRF on an already
        // efficient file can otherwise make it bigger): cap at about 90% of the source rate.
        let cap: string[] = [];
        try {
          const info = await probeMedia(ffmpeg, `input.${ext}`);
          if (info.duration > 0) {
            const kbps = Math.max(300, Math.round((file.size * 8) / info.duration / 1000 * 0.9 - 128));
            cap = ["-maxrate", `${kbps}k`, "-bufsize", `${kbps * 2}k`];
          }
        } catch { /* no cap */ }
        await execWithProgress(
          ffmpeg,
          ["-i", `input.${ext}`, "-c:v", "libx264", "-crf", String(crf), ...cap, "-preset", "superfast", "-pix_fmt", "yuv420p", "-c:a", "aac", "-movflags", "+faststart", "output.mp4"],
          setProgress,
          controller.signal
        );
        const data = await ffmpeg.readFile("output.mp4");
        const blob = ffmpegOutputToBlob(data, "video/mp4");
        setResult({ blob, size: blob.size });
        const change = ((1 - blob.size / file.size) * 100).toFixed(0);
        toast.success(Number(change) > 0 ? `Compressed! Reduced by ${change}%` : "This video was already efficiently encoded, so the result is not smaller. Keep your original.");
      }
    } catch (err) {
      showVideoError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone accept="video/*" maxSize={200} onFiles={handleFiles} label="Drop a video to compress" description="Reduce video file size" gateAtDownload />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card flex justify-between">
            <div><p className="text-sm font-medium">{file.name}</p><p className="text-xs text-muted-foreground">Original: {toMB(file.size, 1)} MB</p></div>
            {result && <p className="text-sm font-medium text-green-600">{toMB(result.size, 1)} MB</p>}
          </div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          {fileProblem && (
            <div role="alert" className="flex gap-2 p-3 rounded-lg border border-destructive/30 bg-destructive/5" data-testid="video-file-problem">
              <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{fileProblem} Choose a complete copy of the video, or press Reset to pick another file.</p>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant={mode === "medium" ? "default" : "outline"} onClick={() => { setMode("medium"); setResult(null); }}>Medium (25% smaller)</Button>
            <Button size="sm" variant={mode === "strong" ? "default" : "outline"} onClick={() => { setMode("strong"); setResult(null); }}>Strong (50% smaller)</Button>
            <Button size="sm" variant={mode === "quality" ? "default" : "outline"} onClick={() => { setMode("quality"); setResult(null); }}>Manual Quality</Button>
            <Button size="sm" variant={mode === "target" ? "default" : "outline"} onClick={() => { setMode("target"); setResult(null); }} className="gap-1.5">
              <Target className="h-3.5 w-3.5" /> Custom Target
            </Button>
          </div>

          {mode === "quality" ? (
            <div className="space-y-2">
              <Label>Compression amount: {quality[0]}%</Label>
              <Slider value={quality} onValueChange={(v) => { setQuality(v); setResult(null); }} min={0} max={100} step={5} />
              {quality[0] >= 67 && <p className="text-xs text-muted-foreground">Higher compression takes noticeably longer to process.</p>}
            </div>
          ) : mode === "medium" || mode === "strong" ? (
            <p className="text-xs text-muted-foreground">
              Targets a real {mode === "medium" ? "25%" : "50%"} smaller file than the original (the encoder aims at the size, and adjusts once if it lands over) — click Compress below.
            </p>
          ) : (
            <div className="space-y-2">
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-xs">Target size (MB)</Label>
                  <Input aria-label="Target size (MB)" type="number" min="1" step="1" value={targetMB} onChange={(e) => { setTargetMB(e.target.value); setResult(null); }} className="w-28" />
                </div>
                <div className="flex gap-1.5 flex-wrap pb-0.5">
                  {TARGET_PRESETS.map((p) => (
                    <Button key={p.label} size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setTargetMB(String(p.bytes / BYTES_PER_MB)); setResult(null); }}>
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex items-end gap-2 flex-wrap" data-testid="compress-trim">
                <div>
                  <Label className="text-xs">Trim start (optional)</Label>
                  <Input aria-label="Trim start" placeholder="0:00" value={trimStart} onChange={(e) => { setTrimStart(e.target.value); setResult(null); }} className="w-24" />
                </div>
                <div>
                  <Label className="text-xs">Trim end (optional)</Label>
                  <Input aria-label="Trim end" placeholder="end" value={trimEnd} onChange={(e) => { setTrimEnd(e.target.value); setResult(null); }} className="w-24" />
                </div>
                <p className="text-xs text-muted-foreground pb-2">File size is bitrate times length, so a shorter clip keeps more quality at the same size.</p>
              </div>
            </div>
          )}

          {mode !== "quality" && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={lowerRes} onChange={(e) => { setLowerRes(e.target.checked); setResult(null); }} />
              Lower resolution to 720p (much faster, and smaller)
            </label>
          )}
          {result && (
            <p className={`text-xs font-medium ${result.metTarget === false || result.size >= file.size ? "text-amber-600" : "text-green-600"}`} data-testid="video-result">
              {toMB(result.size, 2)} MB - {result.size <= file.size ? `${((1 - result.size / file.size) * 100).toFixed(1)}% smaller than` : `${((result.size / file.size - 1) * 100).toFixed(1)}% larger than`} the original ({toMB(file.size, 2)} MB).
              {result.targetBytes !== undefined && (
                result.metTarget
                  ? ` Target reached (${toMB(result.targetBytes, 2)} MB).`
                  : ` Target of ${toMB(result.targetBytes, 2)} MB not reached - this is as small as it got. Try Strong or "Lower resolution to 720p".`
              )}
              {" "}Saved as MP4.
              {result.size >= file.size && " This video was already compressed well, so re-encoding made it bigger. Keep your original; it costs no trial use."}
            </p>
          )}

          {processing && <VideoProcessingProgress heavyNote progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div><p className="text-xs text-muted-foreground mb-1">Original</p><video src={origUrl} controls className="w-full rounded-lg border max-h-56" /></div>
              <div><p className="text-xs text-muted-foreground mb-1">Compressed</p><ResultPreview blob={result.blob} /></div>
            </div>
          )}
          <div className="flex gap-3">
            {result ? (
              <>
                {result.size >= file.size ? (
                  <>
                    <DownloadButton blob={file} fileName={file.name} label="Keep original (download)" freeDownload />
                    <DownloadButton blob={result.blob} fileName={`compressed_${file.name.replace(/\.[^.]+$/, ".mp4")}`} label="Download the larger MP4 anyway" enableShare={false} />
                  </>
                ) : (
                  <DownloadButton blob={result.blob} fileName={`compressed_${file.name.replace(/\.[^.]+$/, ".mp4")}`} />
                )}
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); setLowerRes(false); setFileProblem(null); setTrimStart(""); setTrimEnd(""); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={compress} disabled={processing || !!fileProblem || sizeCheck?.severity !== "ok"}>{processing ? "Compressing..." : "Compress Video"}</Button>
                <Button variant="outline" onClick={() => { setFile(null); setFileProblem(null); }} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
